/**
 * Offline implementation of the v2 Data Studio asset APIs.
 *
 * This mock sits at the **wire** boundary: it returns the same snake_case
 * shapes the live backend returns, so everything above it — clients, mappers,
 * hooks, components — runs identical code in mock and live mode. It therefore
 * implements behaviour, not just fixtures:
 *
 * - real limit/offset slicing, with `meta` computed from the fixture
 *   (`total_count`, `has_next`), never hand-written;
 * - two-dimensional paging on rows, and `column_ids` overriding column paging
 *   with the caller's exact order;
 * - `include` honoured, so a column arrives without profiling unless asked —
 *   a missing `include` fails the same way here as against the backend;
 * - `search` / `asset_type` / `connection_id` / `sort_by` / `sort_order` with
 *   AND-across-params, OR-within-a-list semantics and case-insensitive
 *   name-only matching;
 * - the spec's 400/404 rejections, thrown as axios-shaped `DomainErrorResponse`
 *   failures so `getApiErrorMessage` takes the same branch as it would live;
 * - PATCH curation applied to mutable in-memory state, honouring
 *   omitted-vs-null, so a caller that sends a full body instead of a sparse one
 *   is not silently rewarded.
 */
import {
  mockEmptyUpdateError,
  mockNotFoundError,
  mockNotTabularError,
} from "../mockDomainError";
import { isTabularConnectorAssetType } from "../connectors/connectorAsset.types";
import { hasColumnCurationChanges } from "./dataStudioAssets.types";
import type {
  AssetSearchListResponse,
  AssetSearchResult,
  ColumnCurationUpdate,
  ColumnDetail,
  ColumnListResponse,
  ColumnProfilingSummary,
  ConnectorColumn,
  ListAssetColumnsQuery,
  ListAssetRowsQuery,
  OffsetPaginationMeta,
  RowCellValue,
  RowsResponse,
  SearchAssetsQuery,
  TabularAsset,
} from "./dataStudioAssets.types";

const TENANT_ID = "66b93c85-0e92-4b39-ab63-2965eb1cfdad";

const CONNECTION = {
  snowflake: { id: "11111111-1111-4111-8111-111111111111", type: "snowflake" },
  tableau: { id: "22222222-2222-4222-8222-222222222222", type: "tableau" },
  sheets: { id: "33333333-3333-4333-8333-333333333333", type: "google_sheets" },
} as const;

/** Backend defaults, transcribed from the spec so the mock cannot drift. */
const DEFAULTS = {
  searchLimit: 50,
  columnsLimit: 50,
  rowsLimit: 100,
  columnLimit: 50,
} as const;

const LATENCY_MS = {
  search: 220,
  asset: 180,
  columns: 260,
  rows: 380,
  patch: 240,
} as const;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// ---------------------------------------------------------------------------
// Vega-Lite preview specs
// ---------------------------------------------------------------------------

type LabelledValue = { label: string; value: number };

const barSpec = (values: LabelledValue[]) => ({
  $schema: "https://vega.github.io/schema/vega-lite/v5.json",
  data: { values },
  mark: { type: "bar", tooltip: true },
  encoding: {
    x: { field: "label", type: "nominal" },
    y: { field: "value", type: "quantitative" },
  },
});

const donutSpec = (values: LabelledValue[]) => ({
  $schema: "https://vega.github.io/schema/vega-lite/v5.json",
  data: { values },
  mark: { type: "arc", innerRadius: 50, tooltip: true },
  encoding: {
    theta: { field: "value", type: "quantitative" },
    color: { field: "label", type: "nominal" },
  },
});

/**
 * Numeric histogram — Figma node `5833:113205` ("Table Cell", NUMERIC).
 *
 * Equal-width bins as bottom-aligned rounded bars, with the column's min and
 * max pinned flush to the left and right edges under them. Two things in the
 * design are worth spelling out because they drive the odd-looking numbers
 * below:
 *
 * 1. Bar colour is not positional — it is keyed to the bar's own height. The
 *    design's six bars carry five distinct heights and exactly the five
 *    purple swatches below, darkest on the tallest; the two equal-height bars
 *    share a swatch. Ranking *distinct* counts (rather than binning the raw
 *    count range) is what reproduces that mapping, and it degrades sensibly
 *    for any other bin count.
 * 2. Geometry is expressed as ratios, not the design's literal 32px bar /
 *    8px gap, so the chart still reads correctly at whatever width the
 *    profile grid's column happens to be. A Vega band scale with
 *    `paddingOuter: 0` is the layout the design actually describes (gaps
 *    between bars only, first and last bar flush to the edges), but a band
 *    scale centres its axis labels over the bands, and the design needs the
 *    min/max flush to the plot edges. So the bands are reproduced on a
 *    continuous [0, 1] scale — `step = 1 / (n - paddingInner)`, bar width
 *    `step * (1 - paddingInner)`, which is Vega's own band formula — leaving
 *    `labelFlush` free to do the edge alignment it only does on a continuous
 *    axis.
 */
const NUMERIC_HISTOGRAM_SHADES = [
  "#E9E4F5", // structural/200 — shortest bar
  "#CFC4E7", // primary/200
  "#9780CC", // primary/300
  "#8368C2", // primary/400
  "#6442B3", // primary/500 — tallest bar
];
/** 8px gap over the design's 40px bar-to-bar step. */
const NUMERIC_HISTOGRAM_PADDING_INNER = 0.2;

interface NumericHistogramAxisLabels {
  /** Rendered flush left under the first bar — the design's "$5". */
  min: string;
  /** Rendered flush right under the last bar — the design's "$1890". */
  max: string;
}

const numericHistogramSpec = (
  bins: LabelledValue[],
  axisLabels: NumericHistogramAxisLabels,
) => {
  const darkestShade = NUMERIC_HISTOGRAM_SHADES.length - 1;
  const ascendingCounts = Array.from(
    new Set(bins.map((bin) => bin.value)),
  ).sort((a, b) => a - b);
  /** A single-height histogram has no ranking to express, so it reads as all-max. */
  const shadeOf = (value: number) =>
    ascendingCounts.length < 2
      ? darkestShade
      : Math.round(
          (ascendingCounts.indexOf(value) / (ascendingCounts.length - 1)) *
            darkestShade,
        );

  const step = 1 / (bins.length - NUMERIC_HISTOGRAM_PADDING_INNER);
  const barWidth = step * (1 - NUMERIC_HISTOGRAM_PADDING_INNER);
  const rows = bins.map((bin, index) => ({
    label: bin.label,
    value: bin.value,
    shade: shadeOf(bin.value),
    start: index * step,
    end: index * step + barWidth,
  }));

  return {
    $schema: "https://vega.github.io/schema/vega-lite/v5.json",
    data: { values: rows },
    mark: {
      type: "bar",
      cornerRadius: 4,
      tooltip: true,
    },
    encoding: {
      x: {
        field: "start",
        type: "quantitative",
        scale: { domain: [0, 1], nice: false },
        axis: {
          /** Only the two range ends are labelled; `labelFlush` pins them to the plot edges. */
          values: [0, 1],
          labelExpr: `datum.index === 0 ? ${JSON.stringify(
            axisLabels.min,
          )} : ${JSON.stringify(axisLabels.max)}`,
          labelFlush: true,
          labelAngle: 0,
          /** Figma "Caption 4" — `rawColors.text.mediumEmphasis`. */
          labelPadding: 8,
          labelFont: "Plus Jakarta Sans",
          labelFontSize: 12,
          labelColor: "#566682",
          domain: false,
          ticks: false,
          grid: false,
          title: null,
        },
      },
      x2: { field: "end" },
      /**
       * `y2` is required, not decorative: with both `x` and `x2` bound the bar
       * becomes a ranged rect, and a rect with only one `y` renders at the
       * default continuous band size (~5px) instead of growing from the
       * baseline. Pinning `y2` to 0 restores the column heights.
       */
      y: {
        field: "value",
        type: "quantitative",
        axis: null,
        scale: { domainMin: 0, nice: false },
      },
      y2: { datum: 0 },
      color: {
        field: "shade",
        type: "ordinal",
        scale: {
          domain: NUMERIC_HISTOGRAM_SHADES.map((_, index) => index),
          range: NUMERIC_HISTOGRAM_SHADES,
        },
        legend: null,
      },
      tooltip: [
        { field: "label", type: "nominal", title: "Range" },
        { field: "value", type: "quantitative", title: "Rows", format: "," },
      ],
    },
    view: { stroke: null },
  };
};

/**
 * Ranked horizontal bar list — Figma node `5836:113252` ("Table Cell").
 * Each bar's width is driven by its `value` (not the arbitrary per-row pixel
 * widths in that Figma mock, which don't scale linearly with the sample's
 * own counts — e.g. two 700-count rows there render at different widths).
 * Bars shade darkest-to-lightest by rank using the exact five swatches in
 * that file (`primary/500` → `primary/200` → `structural/200`); the in-bar
 * label flips from white to `text/high-emphasis` once the bar reaches the
 * two lightest shades, matching the contrast switch in the design.
 */
const CATEGORICAL_BAR_RANK_COLORS = [
  "#6442B3", // primary/500
  "#8368C2", // primary/400
  "#9780CC", // primary/300
  "#CFC4E7", // primary/200
  "#E9E4F5", // structural/200
];
const CATEGORICAL_BAR_ROW_HEIGHT = 24;
const CATEGORICAL_BAR_ROW_GAP = 2;
/** Ranks 0-2 sit on the three darkest swatches above and need light label text; rank 3+ sit on the two lightest and need dark text. */
const CATEGORICAL_BAR_LIGHT_TEXT_RANKS = 3;

const categoricalBarListSpec = (categories: LabelledValue[]) => {
  const total = categories.reduce((sum, category) => sum + category.value, 0);
  const maxValue = Math.max(...categories.map((category) => category.value));
  /** 35% of headroom past the longest bar reserves the right-hand percent column. */
  const domainMax = maxValue * 1.35;
  const rowStep = CATEGORICAL_BAR_ROW_HEIGHT + CATEGORICAL_BAR_ROW_GAP;

  const rows = categories.map((category, rank) => ({
    label: category.label,
    value: category.value,
    rank,
    barLabel: `${category.label} (${category.value.toLocaleString()})`,
    percentLabel: `${Math.round((category.value / total) * 100)}%`,
    percentAnchor: domainMax,
    labelColor: rank < CATEGORICAL_BAR_LIGHT_TEXT_RANKS ? "#FFFFFF" : "#1C2535",
  }));

  return {
    $schema: "https://vega.github.io/schema/vega-lite/v5.json",
    height: rows.length * rowStep,
    data: { values: rows },
    layer: [
      {
        mark: {
          type: "bar",
          height: CATEGORICAL_BAR_ROW_HEIGHT,
          cornerRadius: 4,
          tooltip: true,
        },
        encoding: {
          y: { field: "label", type: "nominal", sort: null, axis: null },
          x: {
            field: "value",
            type: "quantitative",
            axis: null,
            scale: { domain: [0, domainMax] },
          },
          color: {
            field: "rank",
            type: "ordinal",
            scale: { range: CATEGORICAL_BAR_RANK_COLORS },
            legend: null,
          },
        },
      },
      {
        /** Figma "Overline 2" — pinned to each bar's left edge via a pixel `x`, independent of bar length. */
        mark: {
          type: "text",
          align: "left",
          dx: 8,
          font: "Plus Jakarta Sans",
          fontWeight: 600,
          fontSize: 10,
        },
        encoding: {
          y: { field: "label", type: "nominal", sort: null },
          x: { value: 0 },
          text: { field: "barLabel" },
          color: { field: "labelColor", type: "nominal", scale: null },
        },
      },
      {
        /** Figma "Caption 3" — shares the bar's x scale so every row's percent lands in one right-aligned column. */
        mark: {
          type: "text",
          align: "right",
          dx: -2,
          font: "Plus Jakarta Sans",
          fontWeight: 500,
          fontSize: 12,
          color: "#1C2535",
        },
        encoding: {
          y: { field: "label", type: "nominal", sort: null },
          x: { field: "percentAnchor", type: "quantitative" },
          text: { field: "percentLabel" },
        },
      },
    ],
    view: { stroke: null },
  };
};

/**
 * Pie + row legend — Figma node `5828:5686` ("Table Cell"), the string-column
 * quality breakdown (Unique / Duplicates / Null). Unlike `categoricalBarListSpec`,
 * slice color here is a *fixed* 3-way semantic mapping (unique is always the
 * darkest of these three swatches, then duplicates, then null), not ranked by
 * value — so the color scale's domain is pinned to that literal key order
 * rather than inferred from the data. `hconcat` places the pie and its legend
 * side by side, matching the design's flex row; there's no need to route
 * `width`/`height` through this spec dynamically the way other helpers do —
 * this is a small, fixed-size widget, not one meant to fill its whole cell.
 */
const STRING_QUALITY_COLORS = {
  unique: "#9780CC", // primary/300
  duplicates: "#CFC4E7", // primary/200
  null: "#E9E4F5", // structural/200
} as const;

interface StringQualityCounts {
  uniqueCount: number;
  duplicateCount: number;
  nullCount: number;
}

const stringQualitySpec = ({
  uniqueCount,
  duplicateCount,
  nullCount,
}: StringQualityCounts) => {
  const total = uniqueCount + duplicateCount + nullCount;
  const rows = (
    [
      { key: "unique", label: "Unique", count: uniqueCount },
      { key: "duplicates", label: "Duplicates", count: duplicateCount },
      { key: "null", label: "Null", count: nullCount },
    ] as const
  ).map((row, rank) => ({
    ...row,
    rank,
    percentLabel: `${Math.round((row.count / total) * 100)}%`,
  }));

  const PIE_SIZE = 108;
  const LEGEND_WIDTH = 132;
  const LEGEND_ROW_HEIGHT = 26;

  return {
    $schema: "https://vega.github.io/schema/vega-lite/v5.json",
    /**
     * Top-level `width`/`height` are what tell `withResponsiveSizing`
     * (DataStudioProfileChart.tsx) this is a fixed-size widget rather than
     * one meant to fill its measured container — omitting them here (only
     * the hconcat children had their own) let the container-height feedback
     * loop that helper otherwise guards against run unchecked.
     */
    width: PIE_SIZE + LEGEND_WIDTH,
    height: PIE_SIZE,
    hconcat: [
      {
        width: PIE_SIZE,
        height: PIE_SIZE,
        data: { values: rows },
        mark: { type: "arc", tooltip: true },
        encoding: {
          theta: { field: "count", type: "quantitative" },
          order: { field: "rank", type: "quantitative" },
          color: {
            field: "key",
            type: "nominal",
            scale: {
              domain: ["unique", "duplicates", "null"],
              range: [
                STRING_QUALITY_COLORS.unique,
                STRING_QUALITY_COLORS.duplicates,
                STRING_QUALITY_COLORS.null,
              ],
            },
            legend: null,
          },
        },
      },
      {
        width: LEGEND_WIDTH,
        height: rows.length * LEGEND_ROW_HEIGHT,
        data: { values: rows },
        layer: [
          {
            /** Legend swatch — same fixed color mapping as the pie slices. */
            mark: {
              type: "rect",
              align: "left",
              width: 12,
              height: 10,
              cornerRadius: 2,
            },
            encoding: {
              y: { field: "label", type: "nominal", sort: null, axis: null },
              x: { value: 0 },
              color: {
                field: "key",
                type: "nominal",
                scale: {
                  domain: ["unique", "duplicates", "null"],
                  range: [
                    STRING_QUALITY_COLORS.unique,
                    STRING_QUALITY_COLORS.duplicates,
                    STRING_QUALITY_COLORS.null,
                  ],
                },
                legend: null,
              },
            },
          },
          {
            /** Figma "Caption 4" — `rawColors.text.mediumEmphasis`. */
            mark: {
              type: "text",
              align: "left",
              dx: 20,
              font: "Plus Jakarta Sans",
              fontSize: 12,
              color: "#566682",
            },
            encoding: {
              y: { field: "label", type: "nominal", sort: null },
              x: { value: 0 },
              text: { field: "label" },
            },
          },
          {
            /** Figma "Caption 2" — `rawColors.text.highEmphasis`. */
            mark: {
              type: "text",
              align: "right",
              font: "Plus Jakarta Sans",
              fontWeight: 600,
              fontSize: 12,
              color: "#1C2535",
            },
            encoding: {
              y: { field: "label", type: "nominal", sort: null },
              x: { value: LEGEND_WIDTH },
              text: { field: "percentLabel" },
            },
          },
        ],
      },
    ],
    center: true,
    spacing: 12,
    config: { view: { stroke: null } },
  };
};

/**
 * Single-metric ring: one filled segment against a track, with the rounded
 * percentage centered inside the hole. Distinct from `donutSpec`, which
 * breaks a column into several *named* categories with a legend — this is
 * for a column-level stat (e.g. `% distinct`) that has no categories at all,
 * just a value against its remainder. Colors are literal hex, mirroring
 * `rawColors.primary[300]` / `primary[100]` — this file mocks the wire
 * response, so it cannot import the frontend's `@magicbi/ui-library` tokens.
 */
const progressRingSpec = (percent: number, label: string) => {
  const clamped = Math.max(0, Math.min(100, percent));
  return {
    $schema: "https://vega.github.io/schema/vega-lite/v5.json",
    /**
     * Top-level `width`/`height` mark this as a fixed-size widget for
     * `withResponsiveSizing` (DataStudioProfileChart.tsx), matching the
     * ring's fixed `outerRadius: 55` (110px diameter) plus room for the two
     * text layers below it. Without these, the helper fell back to filling
     * whatever height the cell measured, which can feed back into a
     * runaway row-height growth loop — see that helper's comment.
     */
    width: 130,
    height: 130,
    layer: [
      {
        data: {
          values: [
            { segment: "value", amount: clamped },
            { segment: "remainder", amount: 100 - clamped },
          ],
        },
        /**
         * `innerRadius`/`outerRadius` are both fixed rather than leaving
         * `outerRadius` to auto-compute from the view: the real profile grid's
         * chart cell is ~128px tall (`DistributionCell`'s 160px minus its
         * padding), so an auto radius lands within a few px of a fixed
         * `innerRadius`, leaving no visible band. Fixing both keeps the ring
         * a consistent, clearly-banded size across cell widths.
         */
        mark: {
          type: "arc",
          innerRadius: 40,
          outerRadius: 55,
          cornerRadius: 4,
          tooltip: false,
        },
        encoding: {
          theta: { field: "amount", type: "quantitative", stack: true },
          order: { field: "segment", sort: "ascending" },
          color: {
            field: "segment",
            type: "nominal",
            scale: {
              domain: ["value", "remainder"],
              range: ["#9780CC", "#F0ECF7"],
            },
            legend: null,
          },
        },
      },
      {
        /** Figma "Caption 1" — mirrors `rawColors.text.interactive` (`--text-interactive`). */
        data: { values: [{ text: `${Math.round(clamped)}%` }] },
        mark: {
          type: "text",
          dy: -10,
          align: "center",
          font: "Plus Jakarta Sans",
          fontSize: 12,
          fontWeight: 700,
          lineHeight: 18,
          color: "#6442B3",
        },
        encoding: { text: { field: "text", type: "nominal" } },
      },
      {
        /** Mirrors `rawColors.text.mediumEmphasis` (`--text-medium-emphasis`). */
        data: { values: [{ text: label }] },
        mark: {
          type: "text",
          dy: 14,
          align: "center",
          font: "Plus Jakarta Sans",
          fontSize: 12,
          color: "#566682",
        },
        encoding: { text: { field: "text", type: "nominal" } },
      },
    ],
    view: { stroke: null },
  };
};

// ---------------------------------------------------------------------------
// Asset fixtures
// ---------------------------------------------------------------------------

interface MockAssetFixture {
  asset:
    | TabularAsset
    | (Omit<TabularAsset, "asset_type"> & { asset_type: string });
  /** Present only for tabular assets; a non-tabular fixture has none. */
  columns?: MockColumnFixture[];
  rowTotal?: number;
  parentAssetId?: string | null;
  qualifiedName: string;
}

interface MockColumnFixture {
  column: ConnectorColumn;
  /** Curated fields, mutated in place by the PATCH mock. */
  curation: Pick<ColumnDetail, "description" | "notes" | "synonyms">;
  /** Deterministic cell value for an absolute row index. */
  cell: (rowIndex: number) => RowCellValue;
}

const CATEGORIES = ["Electronics", "Apparel", "Home", "Beauty"];
const COUNTRIES = ["United States", "Germany", "India", "Brazil"];
const CUSTOMERS = [
  "ava.patel",
  "liam.chen",
  "noah.silva",
  "mia.khan",
  "ethan.brown",
  "zoe.martin",
  "omar.haddad",
  "lucia.rossi",
];

const ORDERS_ROW_TOTAL = 8_900;

const columnFixture = (
  id: string,
  name: string,
  dataType: string,
  ordinal: number,
  options: {
    pk?: boolean;
    fk?: boolean;
    nullable?: boolean;
    score?: number | null;
    summary?: ColumnProfilingSummary;
    spec?: Record<string, unknown>;
    description?: string | null;
    notes?: string[] | null;
    synonyms?: string[] | null;
    cell: (rowIndex: number) => RowCellValue;
  },
): MockColumnFixture => ({
  column: {
    id,
    name,
    data_type: dataType,
    is_nullable: options.nullable ?? true,
    is_primary_key: options.pk ?? false,
    is_foreign_key: options.fk ?? false,
    ordinal_position: ordinal,
    data_quality_score: options.score === undefined ? 92 : options.score,
    profiling_summary: options.summary ?? null,
    profiling_preview_spec: options.spec ?? null,
  },
  curation: {
    description: options.description ?? null,
    notes: options.notes ?? null,
    synonyms: options.synonyms ?? null,
  },
  cell: options.cell,
});

/**
 * Columns of the primary fixture table — one per profiling kind, so every
 * header layout, statistics block, and chart mark is exercised offline.
 */
const ORDERS_COLUMNS: MockColumnFixture[] = [
  columnFixture(
    "aaaaaaaa-0000-4000-8000-000000000001",
    "order_id",
    "varchar(16)",
    1,
    {
      pk: true,
      nullable: false,
      score: 100,
      summary: {
        total_count: ORDERS_ROW_TOTAL,
        distinct_count: ORDERS_ROW_TOTAL,
        null_count: 0,
      },
      spec: progressRingSpec(100, "Distinct"),
      description:
        "Fully unique across all 8,900 rows with zero duplicates. Doubles as the row-level primary key.",
      synonyms: ["order_number"],
      cell: (index) => `ORD-${100_001 + index}`,
    },
  ),
  columnFixture(
    "aaaaaaaa-0000-4000-8000-000000000002",
    "customer_id",
    "integer",
    2,
    {
      fk: true,
      nullable: false,
      score: 97,
      summary: {
        total_count: ORDERS_ROW_TOTAL,
        distinct_count: 2_140,
        null_count: 0,
      },
      notes: [
        "Joins customers.id. Orphan rows were purged in the 2025-11 backfill.",
      ],
      cell: (index) => 5_000 + (index % 2_140),
    },
  ),
  columnFixture(
    "aaaaaaaa-0000-4000-8000-000000000003",
    "product_category",
    "varchar(32)",
    3,
    {
      score: 94,
      summary: {
        total_count: ORDERS_ROW_TOTAL,
        distinct_count: 4,
        null_count: 12,
      },
      spec: categoricalBarListSpec([
        { label: "Electronics", value: 3_120 },
        { label: "Apparel", value: 2_480 },
        { label: "Home", value: 1_960 },
        { label: "Beauty", value: 1_328 },
      ]),
      description: "Merchandising category assigned at checkout.",
      cell: (index) => CATEGORIES[index % CATEGORIES.length],
    },
  ),
  columnFixture(
    "aaaaaaaa-0000-4000-8000-000000000004",
    "order_total",
    "NUMBER(12,2)",
    4,
    {
      nullable: false,
      score: 88,
      summary: {
        total_count: ORDERS_ROW_TOTAL,
        distinct_count: 6_412,
        null_count: 0,
        min: 5.03,
        max: 1_890.99,
        mean: 412.77,
        stddev: 301.44,
      },
      /**
       * Six equal-width bins over the column's own $5.03-$1,890.99 range,
       * with the counts in the profile above them: the design's histogram is
       * an equal-bin one, so the uneven, hand-picked buckets this column used
       * before cannot be drawn by it.
       */
      spec: numericHistogramSpec(
        [
          { label: "$5 - $319", value: 1_293 },
          { label: "$319 - $633", value: 809 },
          { label: "$633 - $947", value: 1_738 },
          { label: "$947 - $1,261", value: 2_255 },
          { label: "$1,261 - $1,576", value: 1_067 },
          { label: "$1,576 - $1,891", value: 1_738 },
        ],
        { min: "$5", max: "$1890" },
      ),
      cell: (index) =>
        Number(
          (5 + ((index * 137) % 1_886) + ((index * 7) % 100) / 100).toFixed(2),
        ),
    },
  ),
  columnFixture(
    "aaaaaaaa-0000-4000-8000-000000000005",
    "quantity",
    "NUMBER(38,0)",
    5,
    {
      nullable: false,
      score: 91,
      summary: {
        total_count: ORDERS_ROW_TOTAL,
        distinct_count: 9,
        null_count: 0,
        min: 1,
        max: 9,
      },
      spec: barSpec([
        { label: "1", value: 3_400 },
        { label: "2", value: 2_100 },
        { label: "3+", value: 3_400 },
      ]),
      cell: (index) => 1 + (index % 9),
    },
  ),
  columnFixture(
    "aaaaaaaa-0000-4000-8000-000000000006",
    "customer_email",
    "varchar(255)",
    6,
    {
      score: 76,
      summary: {
        total_count: ORDERS_ROW_TOTAL,
        distinct_count: 8_000,
        duplicate_count: 612,
        null_count: 288,
      },
      spec: stringQualitySpec({
        uniqueCount: 8_000,
        duplicateCount: 612,
        nullCount: 288,
      }),
      notes: [
        "288 rows have no email — guest checkout was allowed before 2025-06.",
        "612 rows share an email with another row — repeat customers checking out under the same address.",
      ],
      cell: (index) =>
        index % 31 === 0
          ? null
          : `${CUSTOMERS[index % CUSTOMERS.length]}${100_001 + index}@example.com`,
    },
  ),
  columnFixture(
    "aaaaaaaa-0000-4000-8000-000000000007",
    "order_date",
    "TIMESTAMP_NTZ",
    7,
    {
      nullable: false,
      score: 99,
      summary: {
        total_count: ORDERS_ROW_TOTAL,
        distinct_count: 1_460,
        null_count: 0,
        min_value: "2023-01-01T00:00:00Z",
        max_value: "2026-08-31T00:00:00Z",
      },
      spec: barSpec([
        { label: "2023", value: 2_100 },
        { label: "2024", value: 2_320 },
        { label: "2025", value: 2_410 },
        { label: "2026", value: 2_070 },
      ]),
      cell: (index) => {
        const day = String(1 + ((index * 7) % 28)).padStart(2, "0");
        const month = String(1 + (index % 12)).padStart(2, "0");
        return `${2023 + (index % 4)}-${month}-${day}T00:00:00Z`;
      },
    },
  ),
  columnFixture(
    "aaaaaaaa-0000-4000-8000-000000000008",
    "shipping_country",
    "varchar(64)",
    8,
    {
      score: 84,
      summary: {
        total_count: ORDERS_ROW_TOTAL,
        distinct_count: 4,
        null_count: 40,
      },
      spec: donutSpec([
        { label: "United States", value: 4_020 },
        { label: "Germany", value: 2_140 },
        { label: "India", value: 1_680 },
        { label: "Brazil", value: 1_020 },
      ]),
      cell: (index) => COUNTRIES[(index * 3) % COUNTRIES.length],
    },
  ),
  columnFixture(
    "aaaaaaaa-0000-4000-8000-000000000009",
    "is_returned",
    "boolean",
    9,
    {
      nullable: false,
      score: 96,
      summary: {
        total_count: ORDERS_ROW_TOTAL,
        distinct_count: 2,
        null_count: 0,
      },
      spec: donutSpec([
        { label: "Not returned", value: 7_912 },
        { label: "Returned", value: 988 },
      ]),
      cell: (index) => index % 9 === 0,
    },
  ),
  columnFixture(
    "aaaaaaaa-0000-4000-8000-00000000000a",
    "delivery_region",
    "geography",
    10,
    {
      /** Unscored on purpose: the grid must render without a quality strip. */
      score: null,
      cell: (index) => COUNTRIES[(index * 5) % COUNTRIES.length],
    },
  ),
];

const tabularFixture = (
  id: string,
  name: string,
  qualifiedName: string,
  assetType: "relational_table" | "relational_view" | "sheet",
  connector: { id: string; type: string },
  rowTotal: number,
  columns: MockColumnFixture[],
  qualityScore?: number,
): MockAssetFixture => ({
  qualifiedName,
  parentAssetId: null,
  rowTotal,
  columns,
  asset: {
    id,
    tenant_id: TENANT_ID,
    connector: connector as TabularAsset["connector"],
    asset_type: assetType,
    name,
    attributes: {
      row_count: rowTotal,
      column_count: columns.length,
      ...(qualityScore === undefined
        ? {}
        : { data_quality_score: qualityScore }),
    },
  },
});

/** A thin second table, so the panel has more than one selectable asset. */
const USERS_COLUMNS: MockColumnFixture[] = [
  columnFixture(
    "bbbbbbbb-0000-4000-8000-000000000001",
    "user_id",
    "integer",
    1,
    {
      pk: true,
      nullable: false,
      score: 100,
      summary: { total_count: 2_140, distinct_count: 2_140, null_count: 0 },
      cell: (index) => 5_000 + index,
    },
  ),
  columnFixture(
    "bbbbbbbb-0000-4000-8000-000000000002",
    "signup_date",
    "date",
    2,
    {
      nullable: false,
      score: 93,
      summary: { total_count: 2_140, distinct_count: 980, null_count: 0 },
      cell: (index) => `2025-${String(1 + (index % 12)).padStart(2, "0")}-14`,
    },
  ),
  columnFixture(
    "bbbbbbbb-0000-4000-8000-000000000003",
    "plan",
    "varchar(24)",
    3,
    {
      score: 89,
      summary: { total_count: 2_140, distinct_count: 3, null_count: 4 },
      spec: donutSpec([
        { label: "Free", value: 1_240 },
        { label: "Pro", value: 660 },
        { label: "Enterprise", value: 240 },
      ]),
      cell: (index) => ["Free", "Pro", "Enterprise"][index % 3],
    },
  ),
];

const FIXTURES: MockAssetFixture[] = [
  tabularFixture(
    "5f1c2f4e-0000-4000-8000-000000000001",
    "Customer_orders",
    "PROD.SALES.CUSTOMER_ORDERS",
    "relational_table",
    CONNECTION.snowflake,
    ORDERS_ROW_TOTAL,
    ORDERS_COLUMNS,
    92.4,
  ),
  tabularFixture(
    "5f1c2f4e-0000-4000-8000-000000000002",
    "Users",
    "PROD.SALES.USERS",
    "relational_table",
    CONNECTION.snowflake,
    2_140,
    USERS_COLUMNS,
    95.1,
  ),
  tabularFixture(
    "5f1c2f4e-0000-4000-8000-000000000003",
    "Regional_sales_performance",
    "PROD.ANALYTICS.REGIONAL_SALES_PERFORMANCE",
    "relational_view",
    CONNECTION.snowflake,
    412,
    USERS_COLUMNS,
    81.6,
  ),
  tabularFixture(
    "5f1c2f4e-0000-4000-8000-000000000004",
    "Q3 targets",
    "Revenue plan/Q3 targets",
    "sheet",
    CONNECTION.sheets,
    64,
    USERS_COLUMNS,
    /** No aggregate score: the header must render without a quality pill. */
    undefined,
  ),
  /**
   * Non-tabular on purpose. The columns and rows endpoints answer a request
   * for this asset with the spec's 400, which is the only way to exercise that
   * branch offline.
   */
  {
    qualifiedName: "Sales overview/Executive summary",
    parentAssetId: null,
    asset: {
      id: "5f1c2f4e-0000-4000-8000-000000000005",
      tenant_id: TENANT_ID,
      connector: CONNECTION.tableau as TabularAsset["connector"],
      asset_type: "bi_dashboard",
      name: "Executive summary",
    },
  },
];

// ---------------------------------------------------------------------------
// Fixture lookup
// ---------------------------------------------------------------------------

const findFixture = (
  connectionId: string,
  assetId: string,
): MockAssetFixture | undefined =>
  FIXTURES.find(
    (fixture) =>
      fixture.asset.id === assetId &&
      fixture.asset.connector.id === connectionId,
  );

/**
 * Resolves a fixture or throws the spec's failure. A wrong `connectionId` is a
 * 404 rather than a silent cross-connection read, matching a tenant-scoped
 * backend.
 */
const requireFixture = (
  connectionId: string,
  assetId: string,
): MockAssetFixture => {
  const fixture = findFixture(connectionId, assetId);
  if (!fixture) {
    throw mockNotFoundError(
      `Asset ${assetId} was not found on connector connection ${connectionId}.`,
    );
  }
  return fixture;
};

/** Adds the spec's 400 for an asset that has no columns or rows. */
const requireTabularFixture = (
  connectionId: string,
  assetId: string,
): Required<Pick<MockAssetFixture, "columns" | "rowTotal">> &
  MockAssetFixture => {
  const fixture = requireFixture(connectionId, assetId);
  const assetType = fixture.asset.asset_type;
  const tabular =
    isTabularConnectorAssetType(assetType as never) &&
    fixture.columns !== undefined;

  if (!tabular) {
    throw mockNotTabularError(
      `Asset ${assetId} is of type ${assetType} and does not have columns or rows.`,
    );
  }
  return fixture as Required<Pick<MockAssetFixture, "columns" | "rowTotal">> &
    MockAssetFixture;
};

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

const clampLimit = (
  limit: number | undefined,
  fallback: number,
  max: number,
): number => {
  if (typeof limit !== "number" || !Number.isFinite(limit)) return fallback;
  return Math.min(Math.max(Math.trunc(limit), 1), max);
};

const clampOffset = (offset: number | undefined): number => {
  if (typeof offset !== "number" || !Number.isFinite(offset)) return 0;
  return Math.max(Math.trunc(offset), 0);
};

/** Slices a collection and computes `meta` from the slice, never by hand. */
const paginate = <T>(
  items: T[],
  limit: number,
  offset: number,
): { page: T[]; meta: OffsetPaginationMeta } => {
  const page = items.slice(offset, offset + limit);
  return {
    page,
    meta: {
      limit,
      offset,
      total_count: items.length,
      has_next: offset + page.length < items.length,
    },
  };
};

// ---------------------------------------------------------------------------
// A. Search assets
// ---------------------------------------------------------------------------

const toSearchResult = (fixture: MockAssetFixture): AssetSearchResult => ({
  id: fixture.asset.id,
  tenant_id: fixture.asset.tenant_id,
  connector: fixture.asset.connector,
  parent_asset_id: fixture.parentAssetId ?? null,
  asset_type: fixture.asset.asset_type as AssetSearchResult["asset_type"],
  name: fixture.asset.name,
  qualified_name: fixture.qualifiedName,
  is_selected: true,
  is_enabled: true,
  discovered_at: "2026-08-01T10:22:31Z",
});

/**
 * Applies the spec's filter semantics: AND across parameters, OR within one
 * comma-separated list, and a case-insensitive substring match on `name` only
 * — deliberately not `qualified_name`, so a search that works here works live.
 */
const matchesSearchQuery = (
  result: AssetSearchResult,
  query: SearchAssetsQuery,
): boolean => {
  if (
    query.asset_type?.length &&
    !query.asset_type.includes(result.asset_type)
  ) {
    return false;
  }
  if (
    query.connection_id?.length &&
    !query.connection_id.includes(result.connector.id)
  ) {
    return false;
  }
  const search = query.search?.trim().toLowerCase();
  if (search && !result.name.toLowerCase().includes(search)) return false;
  return true;
};

export const searchMockAssets = (
  query: SearchAssetsQuery = {},
): AssetSearchListResponse => {
  const direction = query.sort_order === "desc" ? -1 : 1;
  const matched = FIXTURES.map(toSearchResult)
    .filter((result) => matchesSearchQuery(result, query))
    .sort((left, right) => direction * left.name.localeCompare(right.name));

  const { page, meta } = paginate(
    matched,
    clampLimit(query.limit, DEFAULTS.searchLimit, 200),
    clampOffset(query.offset),
  );

  return { meta, data: page };
};

export const fetchMockAssetSearch = async (
  query: SearchAssetsQuery = {},
): Promise<AssetSearchListResponse> => {
  await wait(LATENCY_MS.search);
  return searchMockAssets(query);
};

// ---------------------------------------------------------------------------
// B. Get asset
// ---------------------------------------------------------------------------

export const getMockAsset = (
  connectionId: string,
  assetId: string,
): TabularAsset => requireFixture(connectionId, assetId).asset as TabularAsset;

export const fetchMockAsset = async (
  connectionId: string,
  assetId: string,
): Promise<TabularAsset> => {
  await wait(LATENCY_MS.asset);
  return getMockAsset(connectionId, assetId);
};

// ---------------------------------------------------------------------------
// C. List columns
// ---------------------------------------------------------------------------

/**
 * Projects a fixture column for the list endpoint. Both profiling fields are
 * *absent* unless `include` names them — the spec omits them by default
 * because they are heavy, and a mock that always sent them would hide every
 * missing-`include` bug until live.
 */
const projectColumn = (
  fixture: MockColumnFixture,
  include: ListAssetColumnsQuery["include"],
): ConnectorColumn => {
  const { profiling_summary, profiling_preview_spec, ...rest } = fixture.column;
  const projected: ConnectorColumn = { ...rest };
  if (include?.includes("profiling_summary")) {
    projected.profiling_summary = profiling_summary ?? null;
  }
  if (include?.includes("profiling_preview_spec")) {
    projected.profiling_preview_spec = profiling_preview_spec ?? null;
  }
  return projected;
};

export const listMockAssetColumns = (
  connectionId: string,
  assetId: string,
  query: ListAssetColumnsQuery = {},
): ColumnListResponse => {
  const fixture = requireTabularFixture(connectionId, assetId);
  const ordered = [...fixture.columns].sort(
    (left, right) =>
      left.column.ordinal_position - right.column.ordinal_position,
  );
  const { page, meta } = paginate(
    ordered,
    clampLimit(query.limit, DEFAULTS.columnsLimit, 200),
    clampOffset(query.offset),
  );

  return { meta, data: page.map((item) => projectColumn(item, query.include)) };
};

export const fetchMockAssetColumns = async (
  connectionId: string,
  assetId: string,
  query: ListAssetColumnsQuery = {},
): Promise<ColumnListResponse> => {
  await wait(LATENCY_MS.columns);
  return listMockAssetColumns(connectionId, assetId, query);
};

// ---------------------------------------------------------------------------
// D / D'. Column detail and curation
// ---------------------------------------------------------------------------

const requireColumnFixture = (
  connectionId: string,
  assetId: string,
  columnId: string,
): MockColumnFixture => {
  const fixture = requireTabularFixture(connectionId, assetId);
  const column = fixture.columns.find((item) => item.column.id === columnId);
  if (!column) {
    throw mockNotFoundError(
      `Column ${columnId} was not found on asset ${assetId}.`,
    );
  }
  return column;
};

/** Detail always carries profiling and curation, nullable when unprofiled. */
const toColumnDetail = (fixture: MockColumnFixture): ColumnDetail => ({
  ...fixture.column,
  profiling_summary: fixture.column.profiling_summary ?? null,
  profiling_preview_spec: fixture.column.profiling_preview_spec ?? null,
  description: fixture.curation.description,
  notes: fixture.curation.notes,
  synonyms: fixture.curation.synonyms,
});

export const getMockColumnDetail = (
  connectionId: string,
  assetId: string,
  columnId: string,
): ColumnDetail =>
  toColumnDetail(requireColumnFixture(connectionId, assetId, columnId));

export const fetchMockColumnDetail = async (
  connectionId: string,
  assetId: string,
  columnId: string,
): Promise<ColumnDetail> => {
  await wait(LATENCY_MS.asset);
  return getMockColumnDetail(connectionId, assetId, columnId);
};

/**
 * Applies a curation PATCH to the in-memory fixture and returns the full,
 * updated column.
 *
 * Semantics are the spec's, applied for real rather than echoed back: a field
 * absent from the body is left alone, a field sent as `null` is cleared, and a
 * list replaces the previous one wholesale. An empty body is the spec's 400.
 */
export const updateMockColumnCuration = (
  connectionId: string,
  assetId: string,
  columnId: string,
  update: ColumnCurationUpdate,
): ColumnDetail => {
  const fixture = requireColumnFixture(connectionId, assetId, columnId);

  if (!hasColumnCurationChanges(update)) {
    throw mockEmptyUpdateError("No recognized fields to update.");
  }

  if (update.description !== undefined)
    fixture.curation.description = update.description;
  if (update.notes !== undefined) fixture.curation.notes = update.notes;
  if (update.synonyms !== undefined)
    fixture.curation.synonyms = update.synonyms;

  return toColumnDetail(fixture);
};

export const patchMockColumnCuration = async (
  connectionId: string,
  assetId: string,
  columnId: string,
  update: ColumnCurationUpdate,
): Promise<ColumnDetail> => {
  await wait(LATENCY_MS.patch);
  return updateMockColumnCuration(connectionId, assetId, columnId, update);
};

// ---------------------------------------------------------------------------
// E. Rows
// ---------------------------------------------------------------------------

/**
 * Selects the columns for a rows page.
 *
 * `column_ids` overrides paging entirely and returns the caller's exact order,
 * including duplicates — the spec says columns and cells come back "in exactly
 * the order given", and an id that does not exist is simply absent rather than
 * an error. Otherwise `column_limit`/`column_offset` page the source order.
 */
const selectRowColumns = (
  columns: MockColumnFixture[],
  query: ListAssetRowsQuery,
): { selected: MockColumnFixture[]; meta: OffsetPaginationMeta } => {
  const ordered = [...columns].sort(
    (left, right) =>
      left.column.ordinal_position - right.column.ordinal_position,
  );

  if (query.column_ids?.length) {
    const byId = new Map(ordered.map((item) => [item.column.id, item]));
    const selected = query.column_ids
      .map((id) => byId.get(id))
      .filter((item): item is MockColumnFixture => item !== undefined);

    return {
      selected,
      meta: {
        limit: selected.length,
        offset: 0,
        total_count: ordered.length,
        has_next: false,
      },
    };
  }

  const { page, meta } = paginate(
    ordered,
    clampLimit(query.column_limit, DEFAULTS.columnLimit, 200),
    clampOffset(query.column_offset),
  );
  return { selected: page, meta };
};

export const listMockAssetRows = (
  connectionId: string,
  assetId: string,
  query: ListAssetRowsQuery = {},
): RowsResponse => {
  const fixture = requireTabularFixture(connectionId, assetId);
  const { selected, meta: columnMeta } = selectRowColumns(
    fixture.columns,
    query,
  );

  const limit = clampLimit(query.limit, DEFAULTS.rowsLimit, 1_000);
  const offset = Math.min(clampOffset(query.offset), fixture.rowTotal);
  const end = Math.min(offset + limit, fixture.rowTotal);

  const data = Array.from(
    { length: Math.max(end - offset, 0) },
    (_, position) => selected.map((column) => column.cell(offset + position)),
  );

  return {
    meta: {
      rows: {
        limit,
        offset,
        total_count: fixture.rowTotal,
        has_next: end < fixture.rowTotal,
      },
      columns: columnMeta,
    },
    columns: selected.map((item) => ({
      id: item.column.id,
      name: item.column.name,
      data_type: item.column.data_type,
    })),
    data,
  };
};

export const fetchMockAssetRows = async (
  connectionId: string,
  assetId: string,
  query: ListAssetRowsQuery = {},
): Promise<RowsResponse> => {
  await wait(LATENCY_MS.rows);
  return listMockAssetRows(connectionId, assetId, query);
};

// ---------------------------------------------------------------------------
// Fixture identity, for stories and tests
// ---------------------------------------------------------------------------

export const MOCK_DATA_STUDIO_IDS = {
  tenantId: TENANT_ID,
  snowflakeConnectionId: CONNECTION.snowflake.id,
  sheetsConnectionId: CONNECTION.sheets.id,
  tableauConnectionId: CONNECTION.tableau.id,
  ordersAssetId: FIXTURES[0].asset.id,
  usersAssetId: FIXTURES[1].asset.id,
  viewAssetId: FIXTURES[2].asset.id,
  sheetAssetId: FIXTURES[3].asset.id,
  /** Non-tabular: the columns and rows endpoints answer this with a 400. */
  dashboardAssetId: FIXTURES[4].asset.id,
  orderIdColumnId: ORDERS_COLUMNS[0].column.id,
  orderTotalColumnId: ORDERS_COLUMNS[3].column.id,
  unscoredColumnId: ORDERS_COLUMNS[9].column.id,
} as const;

export const MOCK_ORDERS_ROW_TOTAL = ORDERS_ROW_TOTAL;

/** Restores curated fields, so a PATCH in one test cannot leak into another. */
export const resetMockDataStudioCuration = (): void => {
  ORDERS_COLUMNS.forEach((item, index) => {
    item.curation = {
      description:
        index === 0
          ? "Fully unique across all 8,900 rows with zero duplicates. Doubles as the row-level primary key."
          : index === 2
            ? "Merchandising category assigned at checkout."
            : null,
      notes:
        index === 1
          ? [
              "Joins customers.id. Orphan rows were purged in the 2025-11 backfill.",
            ]
          : index === 5
            ? [
                "288 rows have no email — guest checkout was allowed before 2025-06.",
              ]
            : null,
      synonyms: index === 0 ? ["order_number"] : null,
    };
  });
  USERS_COLUMNS.forEach((item) => {
    item.curation = { description: null, notes: null, synonyms: null };
  });
};
